import { Button, FormControl, Grid, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';
import React, { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useCustomStore, UseFocus, useMountEffect } from '../../../hooks';
import { TrainingType } from '../../../types';
import { SnackBarConfig } from '../../../utils/SnackBarConfig';
import { InputText } from '../../Forms/InputText';
import { SelectInput } from '../../Forms/selectInput';
import search from './../../../assets/images/h-search.svg';

type SearchFormProps = {
  classes: any;
  onClose: () => void;
};

const TrainingVideosSearchForm: React.FC<any> = (props: SearchFormProps) => {
  const { classes, onClose } = props;
  const { videoStore } = useCustomStore();
  const { enqueueSnackbar } = useSnackbar();
  const [initialFocus, setInitialFocus] = UseFocus();

  const { control, handleSubmit } = useForm<{
    quizTitle: string;
    status: 'active' | 'archived' | 'draft';
    videoTitle: string;
    violationCode: string;
    trainingType: TrainingType;
  }>({
    defaultValues: {
      quizTitle: videoStore.VideoFilters.quizTitle,
      status: videoStore.VideoFilters.status,
      trainingType: videoStore.VideoFilters.trainingType,
      videoTitle: videoStore.VideoFilters.videoTitle,
      violationCode: videoStore.VideoFilters.violationCode,
    },
  });

  useMountEffect(setInitialFocus);

  const onFiltersSubmit = useCallback(
    async (values) => {
      videoStore.setVideoFilters(values);
      videoStore.setShowLoader(true);
      const resp = await videoStore.getInitialTrainings();
      if (resp.isErr()) {
        enqueueSnackbar('Unable to fetch Training Videos', SnackBarConfig('e'));
      }
      videoStore.setShowLoader(false);
      onClose();
    },
    [enqueueSnackbar, onClose, videoStore],
  );

  const resetFilters = useCallback(async () => {
    videoStore.resetVideoFilters();
    videoStore.setShowLoader(true);
    const resp = await videoStore.getInitialTrainings();
    if (resp.isErr()) {
      enqueueSnackbar('Unable to fetch Training Videos', SnackBarConfig('e'));
    }
    videoStore.setShowLoader(false);
    onClose();
  }, [enqueueSnackbar, onClose, videoStore]);

  return (
    <form
      onSubmit={handleSubmit(onFiltersSubmit)}
      id="training-videos-search-form">
      <FormControl fullWidth>
        <Typography component={'h3'} className={classes.filterLabel}>
          Training Status
        </Typography>
        <Grid className={classes.borderBottom}>
          <Grid container className={classes.searcBox}>
            <Grid item md={2} className={classes.dflexJA}>
              <img className={classes.filterIcon} src={search} alt="search" />
            </Grid>
            <Grid item md={10}>
              <Controller
                control={control}
                defaultValue="active"
                name="status"
                render={({ field: { onChange, value, name } }) => {
                  return (
                    <SelectInput
                      name={name}
                      isRefreshValue={true}
                      value={value}
                      size={'small'}
                      onChangeText={onChange}
                      isEditable={false}
                      selectClassName={classes.selectClassName}
                      placeHolder={'Select'}
                      optionsList={
                        <>
                          <option
                            value="active"
                            className={classes.optionsClassName}>
                            Active
                          </option>
                          <option
                            value="archived"
                            className={classes.optionsClassName}>
                            Archived
                          </option>
                          <option
                            value="draft"
                            className={classes.optionsClassName}>
                            Draft
                          </option>
                        </>
                      }
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </FormControl>
      <FormControl fullWidth>
        <Typography component={'h3'} className={classes.filterLabel}>
          Training Name
        </Typography>
        <Grid className={classes.borderBottom}>
          <Grid container className={classes.searcBox}>
            <Grid item md={2} className={classes.dflexJA}>
              <img className={classes.filterIcon} src={search} alt="search" />
            </Grid>
            <Grid item md={10}>
              <Controller
                control={control}
                defaultValue=""
                name="videoTitle"
                render={({ field: { onChange, value, name } }) => {
                  return (
                    <InputText
                      inputRefObj={initialFocus}
                      name={name}
                      isRefreshValue={true}
                      value={value}
                      placeholder="Search"
                      onChangeText={onChange}
                      isEditable={false}
                      className={classes.inputF}
                      type={'text'}
                      variant="outlined"
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </FormControl>
      <FormControl fullWidth>
        <Typography component={'h3'} className={classes.filterLabel}>
          Quiz Title
        </Typography>
        <Grid className={classes.borderBottom}>
          <Grid container className={classes.searcBox}>
            <Grid item md={2} className={classes.dflexJA}>
              <img className={classes.filterIcon} src={search} alt="search" />
            </Grid>
            <Grid item md={10}>
              <Controller
                control={control}
                defaultValue=""
                name="quizTitle"
                render={({ field: { onChange, value, name } }) => {
                  return (
                    <InputText
                      name={name}
                      isRefreshValue={true}
                      value={value}
                      placeholder="Search"
                      onChangeText={onChange}
                      isEditable={false}
                      className={classes.inputF}
                      type={'text'}
                      variant="outlined"
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </FormControl>
      <FormControl fullWidth>
        <Typography component={'h3'} className={classes.filterLabel}>
          Violation Code
        </Typography>
        <Grid className={classes.borderBottom}>
          <Grid container className={classes.searcBox}>
            <Grid item md={2} className={classes.dflexJA}>
              <img className={classes.filterIcon} src={search} alt="search" />
            </Grid>
            <Grid item md={10}>
              <Controller
                control={control}
                defaultValue=""
                name="violationCode"
                render={({ field: { onChange, value, name } }) => {
                  return (
                    <InputText
                      name={name}
                      isRefreshValue={true}
                      value={value}
                      placeholder="Search"
                      onChangeText={onChange}
                      isEditable={false}
                      className={classes.inputF}
                      type={'text'}
                      variant="outlined"
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </FormControl>
      <FormControl fullWidth>
        <Typography component={'h3'} className={classes.filterLabel}>
          Type
        </Typography>
        <Grid className={classes.borderBottom}>
          <Grid container className={classes.searcBox}>
            <Grid item md={2} className={classes.dflexJA}>
              <img className={classes.filterIcon} src={search} alt="search" />
            </Grid>
            <Grid item md={10}>
              <Controller
                control={control}
                name="trainingType"
                defaultValue={TrainingType.ALL}
                render={({ field: { onChange, value, name } }) => {
                  return (
                    <SelectInput
                      name={name}
                      isRefreshValue={true}
                      value={value}
                      size={'small'}
                      onChangeText={onChange}
                      isEditable={false}
                      selectClassName={classes.selectClassName}
                      placeHolder={'Select'}
                      optionsList={
                        <>
                          <option
                            value={TrainingType.ALL}
                            className={classes.optionsClassName}>
                            All
                          </option>
                          <option
                            value={TrainingType.VIDEO}
                            className={classes.optionsClassName}>
                            Video-based
                          </option>
                          <option
                            value={TrainingType.DOCUMENT}
                            className={classes.optionsClassName}>
                            Document-based
                          </option>
                        </>
                      }
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </FormControl>
      <Grid container style={{ padding: '0px !important' }}>
        <Grid item md={6}>
          <Typography component={'div'} className={classes.resetF}>
            <Button
              variant="contained"
              className={' d-flex-ja '.concat(classes.resetF)}
              type="button"
              onClick={resetFilters}
              disabled={videoStore.ShowLoader}>
              RESET
            </Button>
          </Typography>
        </Grid>
        <Grid item md={6}>
          <Typography
            component={'button'}
            className={classes.filterBtn}
            type="submit"
            disabled={videoStore.ShowLoader}>
            SEARCH
          </Typography>
        </Grid>
      </Grid>
    </form>
  );
};

export default observer(TrainingVideosSearchForm);
